import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Share2, Flag, Lock, Info } from "lucide-react";
import { Poll } from "../types";
import { apiService } from "../services/api";
import { authService } from "../services/auth";
import { analyticsService } from "../services/analytics";
import { getCategoryIcon } from "../constants/categories";
import { Avatar } from "./radix/Avatar";
import { Tooltip } from "./radix/Tooltip";
import { AlertDialog } from "./radix/AlertDialog";
import { RadioGroup } from "./radix/RadioGroup";
import { useSignInModal } from "../context/SignInModalContext";
import "./PollCard.css";

interface PollCardProps {
  poll: Poll;
  onVote?: (updatedPoll: Poll) => void;
  onDelete?: (pollId: string) => void;
}

export const PollCard: React.FC<PollCardProps> = ({ poll, onVote, onDelete }) => {
  const [isVoting, setIsVoting] = useState(false);
  const { showSignInModal } = useSignInModal();
  const [error, setError] = useState<string | null>(null);
  const [showCopied, setShowCopied] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [votingOptionId, setVotingOptionId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  
  // Reporting state
  const [showReportConfirm, setShowReportConfirm] = useState(false);
  const [reportReason, setReportReason] = useState<string>("Spam");
  const [isReporting, setIsReporting] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);

  const userInfo = authService.getUserInfo();
  const isOwner = userInfo?.sub === poll.creatorId;
  const isAdminUser = authService.isAdmin();
  const creatorImageUrl = poll.creatorImageUrl ?? (isOwner ? userInfo?.picture : undefined);
  const creatorInitials = (() => {
    const name = poll.creatorName || 'AN';
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
  })();

  // Calculate dynamic font size based on question length
  const getQuestionFontSize = () => {
    const length = poll.question.length;
    if (length < 50) return '26px';
    if (length < 100) return '22px';
    if (length < 150) return '20px';
    if (length < 250) return '18px';
    return '16px';
  };

  const handleVote = async (optionId: string) => {
    if (!userInfo) {
      showSignInModal();
      return;
    }

    if (isVoting) return;

    setIsVoting(true);
    setVotingOptionId(optionId);
    setError(null);

    try {
      const updatedPoll = await apiService.voteOnPoll(poll.pollId, optionId);

      // Track vote in analytics
      try {
        analyticsService.trackVoteSubmitted(
          poll.pollId,
          poll.category || "General",
          optionId,
          userInfo?.sub || "anonymous_user"
        );
      } catch (analyticsErr) {
        console.warn("Failed to track vote in analytics:", analyticsErr);
      }

      // Update poll immediately to show results
      if (onVote) {
        onVote(updatedPoll);
      }
    } catch (err: any) {
      setError(err.message || "Failed to vote");
    } finally {
      setIsVoting(false);
      setVotingOptionId(null);
    }
  };

  const handleUnvote = async () => {
    if (!userInfo) {
      showSignInModal();
      return;
    }

    if (isVoting) return;

    setIsVoting(true);
    setVotingOptionId("unvoting");
    setError(null);

    try {
      const updatedPoll = await apiService.unvoteOnPoll(poll.pollId);
      if (onVote) {
        onVote(updatedPoll);
      }
    } catch (err: any) {
      setError(err.message || "Failed to remove vote");
    } finally {
      setIsVoting(false);
      setVotingOptionId(null);
    }
  };

  const handleShareClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const pollUrl = `${window.location.origin}/feed/${poll.pollId}`;

    try {
      await navigator.clipboard.writeText(pollUrl);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const getPercentage = (voteCount: number): number => {
    if (poll.totalVotes === 0) return 0;
    return Math.round((voteCount / poll.totalVotes) * 100);
  };

  const hasVoted = !!poll.userVote;
  const isClosed = !!poll.isClosed;
  const showResults = hasVoted || isClosed;
  
  return (
    <div className={`poll-card ${isVoting ? "poll-card-voting" : ""} ${isDeleting ? "poll-card-deleting" : ""} ${isClosed ? "poll-card-closed" : ""}`}>
      <div className="poll-header">
        <Link
          to={`/user/${poll.creatorId}`}
          className="poll-creator-wrapper poll-creator-clickable"
        >
          <Avatar
            src={creatorImageUrl}
            fallback={creatorInitials}
            className="poll-creator-avatar"
          />
          <span className="poll-creator">{poll.creatorName || "Anonymous"}</span>
        </Link>
        <div className="poll-header-actions">
          {isOwner && !isClosed && (
            <>
              <button
                className="close-button-action"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCloseConfirm(true);
                }}
                title="Close this poll"
                disabled={isClosing}
              >
                <Lock size={18} />
              </button>
              <AlertDialog
                open={showCloseConfirm}
                onOpenChange={setShowCloseConfirm}
                title="Close Poll?"
                description="Are you sure you want to close this poll? Once closed, no more votes can be cast, and results will be final."
                confirmLabel="Close Poll"
                cancelLabel="Cancel"
                onConfirm={async () => {
                  setIsClosing(true);
                  setError(null);
                  try {
                    const updatedPoll = await apiService.closePoll(poll.pollId);
                    if (onVote) {
                      onVote(updatedPoll);
                    }
                    setShowCloseConfirm(false);
                  } catch (err: any) {
                    setError(err.message || "Failed to close poll");
                    setIsClosing(false);
                    setShowCloseConfirm(false);
                  }
                }}
                onCancel={() => setShowCloseConfirm(false)}
                loading={isClosing}
                variant="danger"
              />
            </>
          )}
          {(isOwner || isAdminUser) && (
            <>
              <button
                className="delete-button"
                onClick={handleDeleteClick}
                title="Delete this poll"
                disabled={isDeleting}
              >
                <Trash2 size={18} />
              </button>
              <AlertDialog
                open={showDeleteConfirm}
                onOpenChange={setShowDeleteConfirm}
                title="Delete Poll?"
                description="This action cannot be undone. The poll and all votes will be permanently deleted."
                confirmLabel="Delete"
                cancelLabel="Cancel"
                onConfirm={async () => {
                  setIsDeleting(true);
                  setError(null);
                  try {
                    await apiService.deletePoll(poll.pollId);
                    if (onDelete) {
                      onDelete(poll.pollId);
                    }
                  } catch (err: any) {
                    setError(err.message || "Failed to delete poll");
                    setIsDeleting(false);
                    setShowDeleteConfirm(false);
                  }
                }}
                onCancel={() => setShowDeleteConfirm(false)}
                loading={isDeleting}
                variant="danger"
              />
            </>
          )}
          {!isOwner && (
            <>
              <button
                className="report-button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!userInfo) {
                    showSignInModal();
                  } else {
                    setShowReportConfirm(true);
                  }
                }}
                title="Report this poll"
                disabled={isReporting}
              >
                <Flag size={18} />
              </button>
              <AlertDialog
                open={showReportConfirm}
                onOpenChange={setShowReportConfirm}
                title="Report Poll?"
                confirmLabel={reportSuccess ? "Reported" : "Report"}
                cancelLabel="Cancel"
                onConfirm={async () => {
                  if (reportSuccess) return;
                  setIsReporting(true);
                  setError(null);
                  try {
                    await apiService.reportPoll(poll.pollId, reportReason);
                    setReportSuccess(true);
                    setTimeout(() => {
                      setReportSuccess(false);
                      setShowReportConfirm(false);
                      if (onDelete) {
                        onDelete(poll.pollId);
                      }
                    }, 1500);
                  } catch (err: any) {
                    setError(err.message || "Failed to report poll");
                    setIsReporting(false);
                  }
                }}
                onCancel={() => {
                  if (!isReporting) {
                    setShowReportConfirm(false);
                  }
                }}
                loading={isReporting}
                variant="danger"
              >
                {reportSuccess ? (
                  <div className="report-success-message">
                    ✓ Poll reported. Thank you for keeping our community safe.
                  </div>
                ) : (
                  <div className="report-modal-body">
                    <p className="alert-dialog-description" style={{ marginBottom: '12px' }}>
                      Please select a reason for reporting this poll. Our team will review this item.
                    </p>
                    <div className="report-reasons-list">
                      {["Spam", "Hate Speech / Harassment", "Inappropriate Content", "Other"].map((reason) => (
                        <label key={reason} className="report-reason-option" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="radio"
                            name="reportReason"
                            value={reason}
                            checked={reportReason === reason}
                            onChange={() => setReportReason(reason)}
                          />
                          <span>{reason}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </AlertDialog>
            </>
          )}
          <Tooltip
            content="✓ Copied"
            open={showCopied}
            onOpenChange={setShowCopied}
          >
            <button
              className="share-button"
              onClick={handleShareClick}
              title="Copy link to poll"
            >
              <Share2 size={18} />
            </button>
          </Tooltip>
          <div className="poll-date">
            {new Date(poll.createdAt).toLocaleDateString()}
          </div>
        </div>
      </div>

      <h3 className="poll-question" style={{ fontSize: getQuestionFontSize() }}>
        {poll.question}
      </h3>

      {error && <div className="error-message">{error}</div>}

      <RadioGroup.Root
        className={`poll-options ${isClosed ? "poll-options-closed" : ""}`}
        value={poll.userVote || ''}
        onValueChange={handleVote}
        disabled={isVoting || isClosed}
      >
        {(() => {
          const maxVotes = Math.max(...poll.options.map((o) => o.voteCount));
          return poll.options.map((option) => {
            const percentage = getPercentage(option.voteCount);
            const isVotingThis = votingOptionId === option.optionId;
            const isUserVote = poll.userVote === option.optionId;
            const isWinner = maxVotes > 0 && option.voteCount === maxVotes;
            return (
              <RadioGroup.Item
                key={option.optionId}
                value={option.optionId}
                className={`poll-option ${
                  !showResults ? "poll-option-unvoted" : ""
                } ${isVotingThis ? "poll-option-voting" : ""} ${isClosed ? "poll-option-closed" : ""} ${isUserVote ? "poll-option-voted" : ""}`}
              >
                {showResults && (
                  <div
                    className={`poll-option-background ${isUserVote ? "voted-background" : ""} ${isWinner ? "winner-background" : ""}`}
                    style={{ width: `${percentage}%` }}
                  />
                )}
                <div className="poll-option-content">
                  <span className="poll-option-text">
                    <RadioGroup.Indicator className="vote-indicator">
                      ✓{' '}
                    </RadioGroup.Indicator>
                    {option.text}
                  </span>
                  {showResults && (
                    <div className="poll-option-result-container">
                      <span className={`poll-option-percentage ${isUserVote ? "voted-percentage" : ""} ${isWinner ? "winner-percentage" : ""}`}>
                        {percentage}%
                        <span className="poll-option-votecount">
                          ({option.voteCount} {option.voteCount === 1 ? "vote" : "votes"})
                        </span>
                        {isWinner && (
                          <span className="winner-crown" title="Leading Choice">
                            👑
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </RadioGroup.Item>
            );
          });
        })()}
      </RadioGroup.Root>

      {isClosed && (
        <div className="poll-closed-details">
          <div className="poll-closed-details-header">
            <p className="poll-closed-message">
              Voting is closed on this poll. You can view the results only.
            </p>
            <Tooltip 
              content="Polls can be closed manually by their creator, or they close automatically after a period of low activity (momentum decay)."
              open={showInfo}
              onOpenChange={setShowInfo}
            >
              <button 
                className="poll-info-trigger" 
                aria-label="Poll closure info"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowInfo(!showInfo);
                }}
              >
                <Info size={18} />
              </button>
            </Tooltip>
          </div>
          {poll.closedAt && (
            <span className="poll-closed-date">
              Closed on {new Date(poll.closedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          )}
        </div>
      )}

      <div className="poll-footer">
        <div className="poll-footer-left">
          {poll.category && (
            <Link
              to={`/explore?category=${encodeURIComponent(poll.category)}`}
              className="poll-category-badge"
            >
              {getCategoryIcon(poll.category)} {poll.category}
            </Link>
          )}
          {showResults && (
            <span className="poll-votes">
              {poll.totalVotes} {poll.totalVotes === 1 ? "vote" : "votes"}
            </span>
          )}
        </div>
        {hasVoted && !isClosed && (
          <button
            className={`unvote-button ${votingOptionId === "unvoting" ? "unvote-button-loading" : ""}`}
            onClick={handleUnvote}
            disabled={isVoting}
            title="Remove your vote"
          >
            {votingOptionId === "unvoting" ? (
              <span className="unvote-spinner"></span>
            ) : (
              "Remove vote"
            )}
          </button>
        )}
      </div>
    </div>
  );
};
